import { useChartTitle, useReportId } from 'hooks/helperHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminOverviewConfig from './AdminOverviewConfig';
import AdminSearchCriteria from './AdminSearchCriteria';
import ChartSidebarSummary from 'components/ReportShelf/ChartSidebarSummary';
import ReportBar from 'components/ReportBar/ReportBar';
import ReportPage from 'components/ReportPage/ReportPage';
import ReportShelf from 'components/ReportShelf/ReportShelf';
import { Typography } from '@trackman/web-shared-components';
import UmpireAdminHeader from 'components/ReportPageHeader/UmpireAdminHeader';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useTranslation } from 'react-i18next';
import useUmpireAdminReport from 'hooks/UmpireReport/AdminOverview/useUmpireAdminReport';
import LoadingModal from 'components/LoadingModal/LoadingModal';
import { useChartVisibility } from 'hooks/useChartVisibility';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';

const UmpireReportAdminOverviewPage = () => {
  const { isCreate } = useReportId();

  const { t } = useTranslation();

  const {
    isEditMode,
    setIsEditMode,

    searchCriteria,
    searchChips,

    tempLayout,
    chartData,

    isLoadingReport,
    generateReport,
    getNewData,
    setTempLayout,

    selectedConfgValues,
    specialConfigOptions,

    saveLayoutForSession,
    saveLayoutForOrg,

    resetStore,
    editingChart,
    setEditingChart,

    hasUnsavedEdits,
    discardLayoutChanges,
  } = useUmpireAdminReport();

  const [openSection, setOpenSection] = useState<'searchCriteria' | 'config'>(isCreate ? 'searchCriteria' : 'config');
  const [parent] = useAutoAnimate();

  const downloadName = useMemo(() => {
    const { leagueNames = [], seasonNames = [] } = searchChips;

    const leagueString = leagueNames ? ' - ' + leagueNames.slice(0, 3).join('; ') + (leagueNames.length > 3 ? '...' : '') : ''; // show first 3 leagues to fit in filename
    const timeString = seasonNames ? seasonNames.slice(0, 3).join(', ') + ' ' : '';

    return `${timeString}Umpire Admin Report${leagueString}`;
  }, [searchChips.leagueNames, searchChips.seasonNames]);

  const { reset: resetChartBrokerStore } = useChartBrokerStore();

  useEffect(() => {
    return () => {
      resetStore();
      resetChartBrokerStore();
    };
  }, [resetStore, resetChartBrokerStore]);

  const { getChartTitle, getChartStats } = useChartTitle();

  const { visibleCharts, hiddenCharts, toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);

  const handleGenerate = useCallback(() => {
    setOpenSection('config');
    generateReport();
  }, [generateReport]);

  const handleToggleVisibility = useCallback(
    (chartId: string) => {
      toggleChartVisibility(chartId);
    },
    [toggleChartVisibility]
  );

  return (
    <>
      <ReportBar
        reportName={t('UmpireReport.AdminOverview')}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        downloadName={downloadName}
        saveLayoutForSession={saveLayoutForSession}
        saveLayoutForOrg={saveLayoutForOrg}
        hasUnsavedEdits={hasUnsavedEdits}
        discardLayoutChanges={discardLayoutChanges}
      />

      <div className='report-layout-content'>
        <ReportShelf
          onGenerate={handleGenerate}
          onModify={getNewData}
          isLoadingReport={isLoadingReport}
          disableGenerate={isLoadingReport || !searchCriteria.leagues || searchCriteria.leagues.length === 0}
          editingChart={editingChart}
          setEditingChart={setEditingChart}
          isEditMode={isEditMode}
        >
          <div className='report-layout-shelf' ref={parent}>
            {tempLayout && isEditMode ? (
              <>
                <div className='report-layout-shelf-section'>
                  {!editingChart && (
                    <Typography component='div' variant='body bold'>
                      {t('Report elements')}
                    </Typography>
                  )}
                  {visibleCharts.map((config) => {
                    const { chartId, chartDrawingInfo, ...extraChartData } = config;
                    const { Sidebar } = chartIdTochartSetup[chartId];
                    if (!editingChart) {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={Sidebar ? setEditingChart : undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={config.isHidden ?? false}
                        />
                      );
                    }
                    return Sidebar && editingChart === chartId ? (
                      <Sidebar
                        extraChartData={extraChartData as never}
                        setTempLayout={setTempLayout as any}
                        tempLayout={tempLayout as any}
                      />
                    ) : null;
                  })}
                </div>
                {!editingChart && hiddenCharts.length > 0 && (
                  <div className='report-layout-shelf-section'>
                    <Typography component='div' variant='body bold'>
                      {t('Extra elements')}
                    </Typography>
                    {hiddenCharts.map((config) => {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={true}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <AdminSearchCriteria
                  isSearchCriteriaOpen={openSection === 'searchCriteria'}
                  setSearchCriteriaOpen={() => setOpenSection('searchCriteria')}
                />
                <AdminOverviewConfig isConfigOpen={openSection === 'config'} setConfigOpen={() => setOpenSection('config')} />
              </>
            )}
          </div>
        </ReportShelf>
        <div className='report-layout-grid-container'>
          {tempLayout &&
            (chartData ?? []).map((chart, i) => (
              <ReportPage
                key={i}
                layout={tempLayout}
                page={i + 1}
                header={
                  <UmpireAdminHeader
                    leagueName={chart.leagueName}
                    seasonNames={searchChips.seasonNames}
                    dateRanges={searchChips.dateRanges}
                    pitchTypes={
                      selectedConfgValues.pitchGrouping === 'Types'
                        ? selectedConfgValues.pitchTagging === 'Auto'
                          ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypes.filter((x) =>
                              selectedConfgValues.pitchTypes.includes(x)
                            ) ?? [])
                          : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypes.filter((x) =>
                              selectedConfgValues.pitchTypes.includes(x)
                            ) ?? [])
                        : undefined
                    }
                    pitchTypeGroups={
                      selectedConfgValues.pitchGrouping === 'Groups'
                        ? selectedConfgValues.pitchTagging === 'Auto'
                          ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypeGroups.filter((x) =>
                              selectedConfgValues.pitchTypeGroups.includes(x)
                            ) ?? [])
                          : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypeGroups.filter((x) =>
                              selectedConfgValues.pitchTypeGroups.includes(x)
                            ) ?? [])
                        : undefined
                    }
                    pitcherHandedness={selectedConfgValues.pitcherHandedness}
                    batterHandedness={selectedConfgValues.batterHandedness}
                    zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                  />
                }
                pageChartData={chart}
                isEditMode={isEditMode}
                isLoadingData={isLoadingReport}
                setTempLayout={setTempLayout}
                setEditingChart={setEditingChart}
                editingChart={editingChart}
              />
            ))}
          {tempLayout && (chartData == null || chartData.length === 0) && (
            <ReportPage
              layout={tempLayout}
              page={1}
              header={
                <UmpireAdminHeader
                  pitchTypes={selectedConfgValues.pitchGrouping === 'Types' ? [] : undefined}
                  pitchTypeGroups={selectedConfgValues.pitchGrouping === 'Groups' ? [] : undefined}
                  pitcherHandedness={selectedConfgValues.pitcherHandedness}
                  batterHandedness={selectedConfgValues.batterHandedness}
                  zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                />
              }
              pageChartData={undefined}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </div>

      <LoadingModal isLoadingReport={isLoadingReport} />
    </>
  );
};

export default UmpireReportAdminOverviewPage;
